import type { GameweekPhase } from '@fantasy/types';

export interface ChipInPlayInput {
  /** The gameweek this specific chip was played in, per FPL's own chip
   * history — `undefined`/`null` when it hasn't been played, or that
   * history isn't known. */
  playedGameweek: number | null | undefined;
  /** `/meta/gameweek`'s own `currentGameweek` — `null`/`undefined` before
   * that fetch has resolved. */
  currentGameweek: number | null | undefined;
  /** `/meta/gameweek`'s own `phase` — `null`/`undefined` alongside
   * `currentGameweek` before that fetch has resolved, or if no gameweek
   * record has a deadline yet. */
  phase: GameweekPhase | null | undefined;
  /** The current gameweek the caller already knows independent of
   * `/meta/gameweek` — `squad.gameweek` for `ChipStatus`, the chip
   * calendar's own first entry for `ChipCalendar`. Used only as a fallback
   * while `currentGameweek`/`phase` are unresolved (loading, or a permanently
   * failed fetch — see `useGameweek`'s `.catch(() => null)`): a chip whose
   * `playedGameweek` matches it is presumed still in play rather than
   * silently demoted to history by a fetch that never came back. Omit it
   * entirely for a caller with no such reference, e.g. an already-known
   * history entry with nothing to presume about. */
  knownCurrentGameweek?: number | null;
}

/**
 * The one place that answers "is this chip in play right now?" (T-119).
 * `ChipStatus` and `ChipCalendar` independently reading `squad.activeChip`
 * and `exclusion.active` is the defect this fixes — not just that one of
 * them was wrong, but that two places were free to disagree. Both call this
 * instead of deriving the answer a second way.
 *
 * A chip is in play only while the gameweek it was played in is *both* the
 * current gameweek and not yet finished — FPL keeps reporting a chip as
 * active for days after its gameweek ends (see T-118), so a raw gameweek
 * match on its own isn't enough. Once `/meta/gameweek` has resolved, this is
 * the whole answer. Before it resolves (or if it never does), a naive
 * "unknown means not in play" quietly demotes a chip that really is active —
 * exactly the case `knownCurrentGameweek` exists for: if the chip's own
 * `playedGameweek` matches it, presume still in play rather than guess
 * "finished". Anything else with an unresolved fetch — a chip with no
 * `knownCurrentGameweek` to compare against, or one that doesn't match it —
 * stays "not in play", which is what lets it render as history instead.
 */
export function isChipInPlay({ playedGameweek, currentGameweek, phase, knownCurrentGameweek }: ChipInPlayInput): boolean {
  if (playedGameweek == null) return false;
  if (currentGameweek != null && phase != null) return playedGameweek === currentGameweek && phase !== 'post';
  return knownCurrentGameweek != null && playedGameweek === knownCurrentGameweek;
}
